import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  InfraManagementApi,
  TDeleteInfraManagementResult,
  TGetLoadBalResult,
  TGetNasVolResult,
  TGetNksResult,
  TSaveLoadBalResult,
  TSaveNasVolResult,
  TSaveNksResult,
} from '../../services';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { ILoadBal, ILoadBalSnapshot, LoadBal } from '../load-bal/LoadBal';
import { INasVol, INasVolSnapshot, NasVol } from '../nas-vol/NasVol';
import { INks, INksSnapshot, Nks } from '../nks/Nks';

/**
 * # InfraManagementStore
 *
 * InfraManagementStore을 설명하세요.
 */
export const InfraManagementStore = types
  .model('InfraManagementStore')
  // --------------------------------------------------------------------------
  .props({
    loadBal: types.optional(types.array(LoadBal), []),
    nasVol: types.optional(types.array(NasVol), []),
    nks: types.optional(types.array(Nks), []),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setLoadBal: (loadBal: ILoadBal[]) => {
      self.loadBal.replace(loadBal);
    },
    setNasVol: (nasVol: INasVol[]) => {
      self.nasVol.replace(nasVol);
    },
    setNks: (nks: INks[]) => {
      self.nks.replace(nks);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    getSections: async (types: Array<'loadBal' | 'nasVol' | 'nks'>) => {
      const infraManagementApi = new InfraManagementApi(self.environment.api);
      const fetchMap = {
        loadBal: () => infraManagementApi.getLoadBal({ useYn: 'Y' }),
        nasVol: () => infraManagementApi.getNasVol({ useYn: 'Y' }),
        nks: () => infraManagementApi.getNks({ useYn: 'Y' }),
      };
      const setterMap = {
        loadBal: self.setLoadBal,
        nasVol: self.setNasVol,
        nks: self.setNks,
      };
      try {
        const promises = types.map(async (type) => {
          const result = await fetchMap[type]();
          self.rootStore.responseStore.getResponseResult(result);

          if (result.kind === 'ok') {
            if (type === 'loadBal' && 'loadBal' in result) {
              setterMap[type](result.loadBal);
            } else if (type === 'nasVol' && 'nasVol' in result) {
              setterMap[type](result.nasVol);
            } else if (type === 'nks' && 'nks' in result) {
              setterMap[type](result.nks);
            }
          } else {
            console.error(`get${type} 실패`, result.kind);
          }
        });

        await Promise.all(promises);
      } catch (e) {
        console.error('getSections error:', e);
      }
    },
    getLoadBal: async (options?: { useYn?: 'Y' | 'N' }) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TGetLoadBalResult = await infraManagementApi.getLoadBal(options);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setLoadBal(result.loadBal);
      } else {
        console.error(result.kind);
      }
    },
    saveLoadBal: async (loadBal: ILoadBalSnapshot) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TSaveLoadBalResult = await infraManagementApi.saveLoadBal(loadBal);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setLoadBal([
          ...self.loadBal.filter((item) => item.loadBalSid !== result.loadBal.loadBalSid),
          result.loadBal,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteLoadBal: async (loadBalSid: number) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TDeleteInfraManagementResult = await infraManagementApi.deleteLoadBal(
        loadBalSid,
      );
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getNasVol: async (options?: { useYn?: 'Y' | 'N' }) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TGetNasVolResult = await infraManagementApi.getNasVol(options);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setNasVol(result.nasVol);
      } else {
        console.error(result.kind);
      }
    },
    saveNasVol: async (nasVol: INasVolSnapshot) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TSaveNasVolResult = await infraManagementApi.saveNasVol(nasVol);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setNasVol([
          ...self.nasVol.filter((item) => item.nasVolSid !== result.nasVol.nasVolSid),
          result.nasVol,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteNasVol: async (nasVolSid: number) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TDeleteInfraManagementResult = await infraManagementApi.deleteNasVol(nasVolSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getNks: async (options?: { useYn?: 'Y' | 'N' }) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TGetNksResult = await infraManagementApi.getNks(options);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setNks(result.nks);
      } else {
        console.error(result.kind);
      }
    },
    saveNks: async (nks: INksSnapshot) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TSaveNksResult = await infraManagementApi.saveNks(nks);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setNks([
          ...self.nks.filter((item) => item.clstrSid !== result.nks.clstrSid),
          result.nks,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteNks: async (nksSid: number) => {
      const infraManagementApi: InfraManagementApi = new InfraManagementApi(self.environment.api);
      const result: TDeleteInfraManagementResult = await infraManagementApi.deleteNks(nksSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TInfraManagementStore = Instance<typeof InfraManagementStore>;
type TInfraManagementStoreSnapshot = SnapshotOut<typeof InfraManagementStore>;

export interface IInfraManagementStore extends TInfraManagementStore {}
export type TInfraManagementStoreKeys = keyof TInfraManagementStoreSnapshot & string;
export interface IInfraManagementStoreSnapshot extends TInfraManagementStoreSnapshot {}
export const createInfraManagementStore = () =>
  types.optional(InfraManagementStore, {} as TInfraManagementStore);
